import { Injectable } from '@angular/core';

/**
 * Used for parsing date strings which could be entered by user in form inputs.
 */
@Injectable({
  providedIn: 'root'
})
export class DateParserService {

  /**
   * @param value in the format 'yyyy-MM-DD'
   * @returns Date or undefined for invalid input
   */
  parseDate(value: string): Date {
    // string check & checking for '-' character
    if (typeof value === 'string' && value.indexOf('-') !== -1) {
      const parts: Array<string> = value.split('-');
      const year: number = parseInt(parts[0], 10);
      const month: number = parseInt(parts[1], 10);
      const day: number = parseInt(parts[2], 10);

      if (!isNaN(month) && !isNaN(year) && !isNaN(day) && month > 0 && month < 13 && year > 0 && day > 0) {
        return new Date(year, month - 1, day);
      }
    }
    return undefined;
  }

  /**
   * @param value in the format 'MM-yyyy'
   * @returns Javascript Date, with day set to first day of the month, or null for invalid input
   */
  parseMonthYearToDate(value: string): Date {
    return this.getDateForMonthYear(value);
  }

  /**
   * @param value in the format 'yyyy-MM'
   * @returns Javascript Date, with day set to first day of the month, or null for invalid input
   */
  parseYearMonthToDate(value: string): Date {
    return this.getDateForMonthYear(value, true);
  }

  /**
   * @param stringValue String value formatted as [0-9][0-9][0-9][0-9]
   * @param nowDate Date to use when calculating current year
   * @returns date object with month/date parsed or null
   *
   * @description
   * Parses MMDD into a valid date. Returns undefined if param value doesn't match
   * the pattern. Resolution for the year:
   * - if passed MMDD >= Current MMDD, than current year is used for the date;
   * - if passed MMDD < Current MMDD, than next year is used for the date.
   *
   * */
  parseMonthDayToDate(stringValue: string, nowDate: Date): Date {
    let month: number;
    let day: number;
    let year: number;

    if (typeof stringValue === 'string' && stringValue.length === 4) {
      month = parseInt(stringValue.substring(0, 2), 10);
      day = parseInt(stringValue.substring(2, 4), 10);
      if (!isNaN(month) && !isNaN(day)) {
        year = nowDate.getFullYear();
        month = month - 1; // for some reason javascript months start with 0
        if ((month < nowDate.getMonth()) || (month === nowDate.getMonth() && day < nowDate.getDate())) {
          year++;
        }
        const result = new Date(year, month, day, 0, 0, 0, 0);
        if (result.getMonth() === month && result.getDate() === day) {
          return result;
        }
      }
    }
    return undefined;
  }

  parseLongMonthDayYearToDate(value: string): Date {
    if(typeof value === 'string') {
      return new Date(value);
    }
  }

  private getDateForMonthYear(value: string, reverseFormat: boolean = false): Date {
    // string check & checking for '-' character
    if (typeof value === 'string' && value.indexOf('-') !== -1) {
      const monthIndex = reverseFormat ? 1 : 0;
      const yearIndex = reverseFormat ? 0 : 1;
      const parts: Array<string> = value.split('-');
      const month: number = parseInt(parts[monthIndex], 10);
      const year: number = parseInt(parts[yearIndex], 10);
      if (!isNaN(month) && !isNaN(year) && month > 0 && month < 13 && year > 0) {
        return new Date(year, month - 1, 1);
      }
    }
    return undefined;
  }
}
